import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Cpu, Plus, X, Send } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import {
  fetchDevices,
  fetchDevicesForGeofence,
  fetchPositions,
  linkGeofenceToDevice,
  unlinkGeofenceFromDevice,
  sendGeofenceConfig,
  type Geofence,
} from '@/lib/traccar-api';

interface GeofenceDevicesDialogProps {
  geofence: Geofence;
  trigger?: React.ReactNode;
}

export function GeofenceDevicesDialog({ geofence, trigger }: GeofenceDevicesDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const { data: allDevices = [], isLoading: loadingAll } = useQuery({
    queryKey: ['devices'],
    queryFn: fetchDevices,
    enabled: open,
  });

  const { data: geofenceDevices = [], isLoading: loadingGeofence } = useQuery({
    queryKey: ['geofenceDevices', geofence.id],
    queryFn: () => fetchDevicesForGeofence(geofence.id),
    enabled: open,
  });

  const { data: positions = [], isLoading: loadingPositions } = useQuery({
    queryKey: ['positions'],
    queryFn: fetchPositions,
    enabled: open,
  });

  const geofenceDeviceIds = new Set(geofenceDevices.map(d => d.id));

  const protocolByDeviceId = new Map(positions.map(p => [p.deviceId, p.protocol]));
  const isRuptela = (deviceId: number) => protocolByDeviceId.get(deviceId) === 'ruptela';

  const linkMutation = useMutation({
    mutationFn: (deviceId: number) => linkGeofenceToDevice(deviceId, geofence.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['geofenceDevices', geofence.id] });
      toast.success(t('geofences.deviceLinked'));
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: (deviceId: number) => unlinkGeofenceFromDevice(deviceId, geofence.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['geofenceDevices', geofence.id] });
      toast.success(t('geofences.deviceUnlinked'));
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const sendMutation = useMutation({
    mutationFn: (deviceId: number) => sendGeofenceConfig(deviceId, geofence),
    onSuccess: () => {
      toast.success(t('geofences.configSent'));
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const availableDevices = allDevices.filter(
    d => !geofenceDeviceIds.has(d.id) && d.name.toLowerCase().includes(search.toLowerCase())
  );

  const isLoading = loadingAll || loadingGeofence || loadingPositions;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="ghost" size="icon" className="h-7 w-7">
            <Cpu className="h-3.5 w-3.5" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('geofences.assignDevices')}</DialogTitle>
          <DialogDescription>
            {t('geofences.assignDevicesDescription', { name: geofence.name })}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium mb-2">{t('geofences.assignedDevices')}</h4>
              {geofenceDevices.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">{t('geofences.noDevicesAssigned')}</p>
              ) : (
                <ScrollArea className="h-[160px] border rounded-md">
                  <div className="p-2 space-y-1">
                    {geofenceDevices.map(device => (
                      <div
                        key={device.id}
                        className="flex items-center justify-between p-2 rounded-md hover:bg-muted/50"
                      >
                        <span className="text-sm">{device.name}</span>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => sendMutation.mutate(device.id)}
                            disabled={sendMutation.isPending || !isRuptela(device.id)}
                            title={!isRuptela(device.id) ? t('geofences.ruptelaOnly') : undefined}
                            className="h-7 gap-1"
                          >
                            <Send className="h-3.5 w-3.5" />
                            {t('geofences.sendConfig')}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => unlinkMutation.mutate(device.id)}
                            disabled={unlinkMutation.isPending}
                            className="h-7 text-destructive hover:text-destructive"
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>

            <div>
              <h4 className="text-sm font-medium mb-2">{t('geofences.availableDevices')}</h4>
              <Input
                placeholder={t('geofences.searchDevices')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="mb-2"
              />
              <ScrollArea className="h-[160px] border rounded-md">
                {availableDevices.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-3">
                    {search ? t('geofences.noDevicesMatch') : t('geofences.allDevicesAssigned')}
                  </p>
                ) : (
                  <div className="p-2 space-y-1">
                    {availableDevices.map(device => (
                      <div
                        key={device.id}
                        className="flex items-center justify-between p-2 rounded-md hover:bg-muted/50"
                      >
                        <span className="text-sm">{device.name}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => linkMutation.mutate(device.id)}
                          disabled={linkMutation.isPending}
                          className="h-7 gap-1"
                        >
                          <Plus className="h-3.5 w-3.5" />
                          {t('common.add')}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
